/**
 * AI perception (spec §7): who is alive, who is an enemy, the nearest enemy, where a threat sits relative
 * to the nose, and the pilots' cursor into the combat event ring. Allocation-free (D-014).
 *
 * Additive (M3 R19, D-TBD 12): optional siege objectives (`AiShips.objectives`, entities with a
 * `SiegeObjective`, such as a rebooting relay). A pilot weighs each live objective of another team at
 * distance / weight against its nearest enemy ship. Without objectives every result is M1's.
 */
import type { EventRing } from '../combat/events'
import { isHostile } from '../combat/hostility'
import { rotateVec, type Quat, type Vec3 } from '../core/math'
import { SiegeObjective } from '../reach/components'
import { Hull, Life, Pos64, Rot, Team } from '../world/components'
import type { AiShips } from './pilot'

/** The AI's own reader of the combat event ring (plan §2.5: every reader keeps its own cursor). */
export interface AiEventReader {
  ring: EventRing
  /** Next sequence number to read. */
  cursor: number
}

/** A reader that starts at the ring's current head: pilots react only to events emitted from now on. */
export function createAiEventReader(ring: EventRing): AiEventReader {
  return { ring, cursor: ring.head }
}

// Module scratch (D-014).
const q: Quat = { x: 0, y: 0, z: 0, w: 1 }
const fwd: Vec3 = { x: 0, y: 0, z: 0 }

/** True when `eid` is one of the live ships in `all` and has not died (`Life.alive`). */
export function isLiveShip(all: ArrayLike<number>, eid: number): boolean {
  if (eid < 0 || Life.alive[eid] !== 1) return false
  for (let i = 0; i < all.length; i++) if (all[i] === eid) return true
  return false
}

/** Squared distance between two ships, float64 (D-011). */
export function distanceSq(a: number, b: number): number {
  const dx = Pos64.x[b] - Pos64.x[a]
  const dy = Pos64.y[b] - Pos64.y[a]
  const dz = Pos64.z[b] - Pos64.z[a]
  return dx * dx + dy * dy + dz * dz
}

/** The nearest living ship of a team hostile to `eid`'s (`isHostile`, M4 I1), or -1. Ties go to the earlier entry of `all` (deterministic). */
export function nearestEnemy(all: ArrayLike<number>, eid: number): number {
  const team = Team.id[eid]
  let best = -1
  let bestD2 = Infinity
  for (let i = 0; i < all.length; i++) {
    const other = all[i]
    if (other === eid || !isHostile(team, Team.id[other]) || Life.alive[other] !== 1) continue
    const d2 = distanceSq(eid, other)
    if (d2 < bestD2) {
      bestD2 = d2
      best = other
    }
  }
  return best
}

/**
 * Additive (M3 R19): true when `eid` is one of `objectives` and still standing (`Life.alive`, hull left).
 * Always false without objectives.
 */
export function isLiveObjective(objectives: ArrayLike<number> | undefined, eid: number): boolean {
  if (objectives === undefined || eid < 0 || Life.alive[eid] !== 1 || Hull.value[eid] <= 0) return false
  for (let i = 0; i < objectives.length; i++) if (objectives[i] === eid) return true
  return false
}

/** Additive (M3 R19): a live ship of `ships.all`, or a live siege objective. Without objectives, `isLiveShip`. */
export function isLiveTarget(ships: AiShips, eid: number): boolean {
  return isLiveShip(ships.all, eid) || isLiveObjective(ships.objectives, eid)
}

/**
 * Additive (M3 R19): `nearestEnemy`, unless a live siege objective of another team weighs less. Each objective
 * counts at distance / `SiegeObjective.weight` (a weight of 2 draws a raider to a relay twice as far away as the
 * player); the ship keeps a tie, and an earlier objective keeps its tie. Without objectives this is `nearestEnemy`.
 */
export function nearestEnemyOrObjective(ships: AiShips, eid: number): number {
  const ship = nearestEnemy(ships.all, eid)
  const objectives = ships.objectives
  if (objectives === undefined || objectives.length === 0) return ship
  const team = Team.id[eid]
  let best = ship
  let bestScore = ship >= 0 ? distanceSq(eid, ship) : Infinity
  for (let i = 0; i < objectives.length; i++) {
    const o = objectives[i]
    const w = SiegeObjective.weight[o]
    if (o === eid || !isHostile(team, Team.id[o]) || Life.alive[o] !== 1 || Hull.value[o] <= 0 || !(w > 0)) continue
    // Squared: (d / w)² against the ship's d².
    const score = distanceSq(eid, o) / (w * w)
    if (score < bestScore) {
      bestScore = score
      best = o
    }
  }
  return best
}

/** Writes ship `eid`'s nose direction (local −Z in world space) into `out`. */
export function forwardOf(eid: number, out: Vec3): Vec3 {
  q.x = Rot.x[eid]
  q.y = Rot.y[eid]
  q.z = Rot.z[eid]
  q.w = Rot.w[eid]
  return rotateVec(q, 0, 0, -1, out)
}

/** True when the world point (x, y, z) lies behind ship `eid`'s nose plane (the rear hemisphere). */
export function isInRearHemisphere(eid: number, x: number, y: number, z: number): boolean {
  forwardOf(eid, fwd)
  return fwd.x * (x - Pos64.x[eid]) + fwd.y * (y - Pos64.y[eid]) + fwd.z * (z - Pos64.z[eid]) < 0
}
